import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAppWorkspace } from "@/contexts/AppWorkspaceContext";
import { ServerWithName } from "@/hooks/use-app-state";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MoreVertical,
  Link2Off,
  Download,
  Loader2,
  ChevronDown,
  ChevronRight,
  Plus,
  Database,
} from "lucide-react";
import { toast } from "sonner";
import { useMutation, useQuery } from "convex/react";
import { api } from "@/convex/src/_generated/api";
import { getStoredTokens } from "@/lib/mcp-oauth";
import { decodeJWT } from "@/lib/jwt-decoder";
import JsonView from "react18-json-view";
import "react18-json-view/src/style.css";
import "react18-json-view/src/dark.css";
import {
  listTools,
  type ListToolsResultWithMetadata,
} from "@/lib/mcp-tools-api";
import { AuthenticationSection } from "../connection/shared/AuthenticationSection";
import { CustomHeadersSection } from "../connection/shared/CustomHeadersSection";
import { EnvVarsSection } from "../connection/shared/EnvVarsSection";
import { useServerForm } from "../connection/hooks/use-server-form";
import { ServerFormData } from "@/shared/types.js";
import { getConnectionStatusMeta } from "../connection/server-card-utils";
import { AddServerModal } from "../connection/AddServerModal";
import { MCPIcon } from "../ui/mcp-icon";
import { Card } from "../ui/card";

interface MCPServerSettingsSectionProps {
  connectedServerConfigs: Record<string, ServerWithName>;
  onConnect: (formData: ServerFormData) => void;
  onDisconnect: (serverName: string) => void;
  onReconnect: (serverName: string) => void;
  onUpdate?: (
    originalServerName: string,
    formData: ServerFormData,
    skipAutoConnect?: boolean,
  ) => void;
  onRemove?: (serverName: string) => void;
}

export function MCPServerSettingsSection({
  connectedServerConfigs,
  onConnect,
  onDisconnect,
  onReconnect,
  onUpdate,
  onRemove,
}: MCPServerSettingsSectionProps) {
  const { activeWorkspace } = useAppWorkspace();
  const setPrimaryServerMutation = useMutation(api.workspaces.setPrimaryServer);
  const removeServerMutation = useMutation(
    api.appWorkspaceServers.removeServer,
  );

  // Query active servers from database to determine if any exist
  const activeServers = useQuery(
    api.appWorkspaceServers.listServers,
    activeWorkspace?._id ? { workspaceId: activeWorkspace._id } : "skip",
  );

  const [activeTab, setActiveTab] = useState<
    "overview" | "config" | "auth" | "tools"
  >("overview");
  const [isExporting, setIsExporting] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [tools, setTools] = useState<ListToolsResultWithMetadata | null>(null);
  const [isLoadingTools, setIsLoadingTools] = useState(false);
  const [toolsError, setToolsError] = useState<string | null>(null);
  const [showConfiguration, setShowConfiguration] = useState(false);
  const [hasConfigChanges, setHasConfigChanges] = useState(false);
  const [hasAuthChanges, setHasAuthChanges] = useState(false);
  const [isAddingServer, setIsAddingServer] = useState(false);

  if (!activeWorkspace) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">MCP Server</h1>
        <p className="text-sm text-muted-foreground">No workspace selected</p>
      </div>
    );
  }

  const primaryServerId = activeWorkspace.primaryServerId;

  // Find the primary server by ID from activeServers, then look up in connectedServerConfigs by name
  const primaryServerRecord =
    primaryServerId && activeServers
      ? activeServers.find((s) => s._id === primaryServerId)
      : null;

  const primaryServerName = primaryServerRecord?.name;
  const primaryServer = primaryServerName
    ? connectedServerConfigs[primaryServerName]
    : null;

  // If we have a primaryServerId but no server record yet, it's still loading
  const isServerLoading = primaryServerId && !primaryServerRecord;

  // Use the shared form hook for editing
  const {
    serverFormData,
    setServerFormData,
    commandInput,
    setCommandInput,
    oauthScopesInput,
    setOauthScopesInput,
    clientId,
    setClientId,
    clientSecret,
    setClientSecret,
    bearerToken,
    setBearerToken,
    authType,
    setAuthType,
    useCustomClientId,
    setUseCustomClientId,
    requestTimeout,
    setRequestTimeout,
    clientIdError,
    setClientIdError,
    clientSecretError,
    setClientSecretError,
    envVars,
    customHeaders,
    showAuthSettings,
    setShowAuthSettings,
    addEnvVar,
    removeEnvVar,
    updateEnvVar,
    addCustomHeader,
    removeCustomHeader,
    updateCustomHeader,
    buildFormData,
    validateClientId,
    validateClientSecret,
  } = useServerForm(primaryServer || undefined);

  // Load tools when switching to tools tab
  useEffect(() => {
    const loadTools = async () => {
      if (!primaryServer || primaryServer.connectionStatus !== "connected") {
        setToolsError("not-connected");
        return;
      }

      setIsLoadingTools(true);
      setToolsError(null);

      try {
        const result = await listTools(primaryServer.name);
        setTools(result);
      } catch (error) {
        console.error("Failed to load tools:", error);
        setToolsError(
          error instanceof Error ? error.message : "Failed to load tools",
        );
      } finally {
        setIsLoadingTools(false);
      }
    };

    if (activeTab === "tools" && primaryServer && !tools) {
      loadTools();
    }
  }, [activeTab, primaryServer]);

  const handleRemoveServer = async () => {
    if (!primaryServerName) return;

    try {
      // First, archive the server in the database
      await removeServerMutation({
        workspaceId: activeWorkspace._id,
        name: primaryServerName,
      });

      // Then clear it as primary
      await setPrimaryServerMutation({
        workspaceId: activeWorkspace._id,
        serverId: undefined,
      });

      // Disconnect and remove from local state
      if (onDisconnect) {
        onDisconnect(primaryServerName);
      }
      if (onRemove) {
        onRemove(primaryServerName);
      }

      toast.success("Server removed");
    } catch (error) {
      console.error("Failed to remove server:", error);
      toast.error("Failed to remove server");
    }
  };

  const handleExport = async () => {
    if (!primaryServer) return;
    setIsExporting(true);
    try {
      // Export logic would go here
      toast.success("Server config exported");
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export server config");
    } finally {
      setIsExporting(false);
    }
  };

  const handleSaveConfig = () => {
    if (!primaryServer || !onUpdate) return;

    const finalFormData = buildFormData();
    onUpdate(primaryServer.name, finalFormData, true);
    toast.success("Configuration updated");
    // Reset change tracking
    setHasConfigChanges(false);
    setHasAuthChanges(false);
  };

  const handleAddServer = async (formData: ServerFormData) => {
    setIsAddingServer(false);

    try {
      // Add the server (now saves to Convex immediately, regardless of connection success)
      await onConnect(formData);

      // Then set as primary in database
      await setPrimaryServerMutation({
        workspaceId: activeWorkspace._id,
        serverName: formData.name,
      });

      toast.success(`"${formData.name}" added as primary server`);
    } catch (error) {
      console.error("Failed to add server:", error);
      toast.error("Failed to add server");
    }
  };

  // Initialize server when primary server loads
  useEffect(() => {
    if (primaryServer) {
      setHasConfigChanges(false);
      setHasAuthChanges(false);
    }
  }, [primaryServer?.name]);

  const handleReconnect = async () => {
    if (!primaryServerName) return;
    setIsReconnecting(true);
    try {
      await onReconnect(primaryServerName);
    } finally {
      // Keep loading state for a moment to show feedback
      setTimeout(() => setIsReconnecting(false), 500);
    }
  };

  // Track changes for config tab
  useEffect(() => {
    if (!primaryServer) {
      setHasConfigChanges(false);
      return;
    }

    // Check basic fields
    let hasChanged = serverFormData.name !== primaryServer.name;

    // Check command/URL based on type
    if ("command" in primaryServer.config) {
      const commandChanged = commandInput !== primaryServer.config.command;
      const envChanged =
        JSON.stringify(envVars) !==
        JSON.stringify(primaryServer.config.env || []);
      hasChanged = hasChanged || commandChanged || envChanged;
    } else if ("url" in primaryServer.config) {
      const urlChanged =
        serverFormData.url !== String(primaryServer.config.url);
      const headersChanged =
        JSON.stringify(customHeaders) !==
        JSON.stringify(primaryServer.config.headers || []);
      hasChanged = hasChanged || urlChanged || headersChanged;
    }

    // Check timeout (compare as numbers since requestTimeout is stored as string)
    const timeoutChanged =
      Number(requestTimeout) !== (primaryServer.config.requestTimeout || 60000);
    hasChanged = hasChanged || timeoutChanged;

    setHasConfigChanges(hasChanged);
  }, [
    primaryServer,
    serverFormData,
    commandInput,
    envVars,
    customHeaders,
    requestTimeout,
  ]);

  // Track changes for auth tab
  useEffect(() => {
    if (!primaryServer) {
      setHasAuthChanges(false);
      return;
    }

    // Simple change detection - mark as changed if any auth field is modified
    const hasChanged =
      authType !==
        (primaryServer.config.useOAuth
          ? "oauth"
          : bearerToken
            ? "bearer"
            : "none") ||
      (authType === "bearer" && bearerToken !== "") ||
      (authType === "oauth" && (oauthScopesInput !== "" || useCustomClientId));

    setHasAuthChanges(hasChanged);
  }, [
    primaryServer,
    authType,
    bearerToken,
    oauthScopesInput,
    clientId,
    clientSecret,
    useCustomClientId,
  ]);

  // Server is loading (primary set but config not loaded yet)
  // Show this briefly, but if it persists, it means the server was never added
  if (isServerLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">MCP Server</h1>
          <div className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-muted/50 border border-border">
            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">
              Loading...
            </span>
          </div>
        </div>
        <Card className="p-12 text-center">
          <Loader2 className="mx-auto h-12 w-12 text-muted-foreground animate-spin" />
          <p className="mt-4 text-sm text-muted-foreground">
            Setting up primary server...
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={async () => {
              try {
                await setPrimaryServerMutation({
                  workspaceId: activeWorkspace._id,
                  serverId: undefined,
                });
                toast.info("Primary server cleared");
              } catch (error) {
                console.error("Failed to clear primary server:", error);
              }
            }}
          >
            Cancel
          </Button>
        </Card>
      </div>
    );
  }

  // No primary server set
  if (!primaryServerId || !primaryServer) {
    // Check if there are any active (non-archived) servers in the database
    const hasActiveServers = activeServers && activeServers.length > 0;

    return (
      <>
        <div className="space-y-6">
          <h1 className="text-2xl font-bold mb-6">MCP Server</h1>

          <Card className="p-12 text-center">
            <div className="mx-auto max-w-sm">
              {hasActiveServers ? (
                <Database className="mx-auto h-12 w-12 text-muted-foreground" />
              ) : (
                <MCPIcon className="mx-auto h-12 w-12 text-muted-foreground" />
              )}
              <h3 className="mt-4 text-lg font-semibold">
                {hasActiveServers
                  ? "No primary server set"
                  : "Connect your MCP server"}
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                {hasActiveServers
                  ? "Select a primary server from your workspace or add a new one to get started"
                  : "Get started by creating an MCP Client-Server connection"}
              </p>
              <div className="flex gap-2 justify-center mt-4">
                <Button
                  onClick={() => setIsAddingServer(true)}
                  className="cursor-pointer"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Server
                </Button>
                {hasActiveServers && (
                  <Button
                    variant="outline"
                    onClick={() => (window.location.hash = "servers")}
                    className="cursor-pointer"
                  >
                    Go to Workspaces
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </div>

        <AddServerModal
          isOpen={isAddingServer}
          onClose={() => setIsAddingServer(false)}
          onSubmit={handleAddServer}
        />
      </>
    );
  }

  const isConnected = primaryServer.connectionStatus === "connected";
  const {
    label: connectionStatusLabel,
    Icon: ConnectionStatusIcon,
    iconClassName,
    indicatorColor,
  } = getConnectionStatusMeta(primaryServer.connectionStatus);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">MCP Server</h1>
          <div className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-muted/50 border border-border">
            <div
              className="h-2 w-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: indicatorColor }}
            />
            <ConnectionStatusIcon className={iconClassName} />
            <span className="text-xs font-medium text-muted-foreground">
              {connectionStatusLabel}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleReconnect} disabled={isReconnecting}>
            {isReconnecting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Reconnecting...
              </>
            ) : isConnected ? (
              "Reconnect"
            ) : (
              "Connect"
            )}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onDisconnect(primaryServerId)}>
                <Link2Off className="h-4 w-4 mr-2" />
                Disconnect
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleExport}
                disabled={isExporting || !isConnected}
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Export Config
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleRemoveServer}
                className="text-destructive"
              >
                <Link2Off className="h-4 w-4 mr-2" />
                Remove
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        {[
          { id: "overview", label: "Overview" },
          { id: "config", label: "Configuration" },
          { id: "auth", label: "Authentication" },
          { id: "tools", label: "Widget Metadata" },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id as any)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? "border-b-2 border-primary text-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="py-4">
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-4 max-w-2xl">
            <div>
              <h3 className="text-lg font-semibold mb-2">
                {primaryServer.name}
              </h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Status:</span>
                  <Badge
                    variant={
                      primaryServer.connectionStatus === "connected"
                        ? "default"
                        : "secondary"
                    }
                  >
                    {primaryServer.connectionStatus}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Type:{" "}
                  {"command" in primaryServer.config ? "STDIO" : "HTTP (SSE)"}
                </p>
                {"command" in primaryServer.config && (
                  <div className="mt-2">
                    <span className="text-sm text-muted-foreground">
                      Command:
                    </span>
                    <code className="block mt-1 p-2 bg-muted rounded text-xs">
                      {primaryServer.config.command}
                    </code>
                  </div>
                )}
                {"url" in primaryServer.config && (
                  <div className="mt-2">
                    <span className="text-sm text-muted-foreground">URL:</span>
                    <code className="block mt-1 p-2 bg-muted rounded text-xs">
                      {String(primaryServer.config.url)}
                    </code>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Configuration Tab */}
        {activeTab === "config" && (
          <div className="space-y-6 max-w-2xl">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Server Name
              </label>
              <Input
                value={serverFormData.name}
                onChange={(e) =>
                  setServerFormData((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                placeholder="my-mcp-server"
                className="h-10"
              />
            </div>

            {/* Connection Type */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Connection Type
              </label>
              {serverFormData.type === "stdio" ? (
                <div className="flex">
                  <Select
                    value={serverFormData.type}
                    onValueChange={(value: "stdio" | "http") =>
                      setServerFormData((prev) => ({
                        ...prev,
                        type: value,
                      }))
                    }
                  >
                    <SelectTrigger className="w-22 rounded-r-none border-r-0 text-xs border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="stdio">STDIO</SelectItem>
                      <SelectItem value="http">HTTP</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    value={commandInput}
                    onChange={(e) => setCommandInput(e.target.value)}
                    placeholder="npx -y @modelcontextprotocol/server-everything"
                    className="flex-1 rounded-l-none text-sm border-border"
                  />
                </div>
              ) : (
                <div className="flex">
                  <Select
                    value={serverFormData.type}
                    onValueChange={(value: "stdio" | "http") =>
                      setServerFormData((prev) => ({
                        ...prev,
                        type: value,
                      }))
                    }
                  >
                    <SelectTrigger className="w-22 rounded-r-none border-r-0 text-xs border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="stdio">STDIO</SelectItem>
                      <SelectItem value="http">HTTP</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    value={serverFormData.url}
                    onChange={(e) =>
                      setServerFormData((prev) => ({
                        ...prev,
                        url: e.target.value,
                      }))
                    }
                    placeholder="http://localhost:8080/mcp"
                    className="flex-1 rounded-l-none text-sm border-border"
                  />
                </div>
              )}
            </div>

            {/* Environment Variables for STDIO */}
            {serverFormData.type === "stdio" && (
              <EnvVarsSection
                envVars={envVars}
                showEnvVars={true}
                onToggle={() => {}}
                onAdd={addEnvVar}
                onRemove={removeEnvVar}
                onUpdate={updateEnvVar}
              />
            )}

            {/* Custom Headers for HTTP */}
            {serverFormData.type === "http" && (
              <CustomHeadersSection
                customHeaders={customHeaders}
                showCustomHeaders={true}
                onToggle={() => {}}
                onAdd={addCustomHeader}
                onRemove={removeCustomHeader}
                onUpdate={updateCustomHeader}
              />
            )}

            {/* Additional Configuration Section */}
            <div className="border border-border rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowConfiguration(!showConfiguration)}
                className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  {showConfiguration ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="text-sm font-medium text-foreground">
                    Additional Configuration
                  </span>
                </div>
              </button>

              {showConfiguration && (
                <div className="p-4 space-y-4 border-t border-border bg-muted/30">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-foreground">
                      Request Timeout
                    </label>
                    <Input
                      type="number"
                      value={requestTimeout}
                      onChange={(e) =>
                        setRequestTimeout(Number(e.target.value))
                      }
                      placeholder="60000"
                      className="h-10"
                    />
                    <p className="text-xs text-muted-foreground">
                      Maximum time (in milliseconds) to wait for a response
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end pt-4">
              <Button onClick={handleSaveConfig} disabled={!hasConfigChanges}>
                Save Changes
              </Button>
            </div>
          </div>
        )}

        {/* Authentication Tab */}
        {activeTab === "auth" && (
          <div className="space-y-6 max-w-2xl">
            <div>
              <h3 className="text-lg font-semibold">Authentication Settings</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Configure authentication for your MCP server connection
              </p>
            </div>

            {serverFormData.type === "http" ? (
              <div className="space-y-4">
                <AuthenticationSection
                  authType={authType}
                  onAuthTypeChange={(value) => {
                    setAuthType(value);
                    setShowAuthSettings(value !== "none");
                    if (value === "oauth") {
                      setServerFormData((prev) => ({
                        ...prev,
                        useOAuth: true,
                      }));
                    } else {
                      setServerFormData((prev) => ({
                        ...prev,
                        useOAuth: false,
                      }));
                    }
                  }}
                  showAuthSettings={showAuthSettings}
                  bearerToken={bearerToken}
                  onBearerTokenChange={setBearerToken}
                  oauthScopesInput={oauthScopesInput}
                  onOauthScopesChange={setOauthScopesInput}
                  useCustomClientId={useCustomClientId}
                  onUseCustomClientIdChange={(checked) => {
                    setUseCustomClientId(checked);
                    if (!checked) {
                      setClientId("");
                      setClientSecret("");
                      setClientIdError(null);
                      setClientSecretError(null);
                    }
                  }}
                  clientId={clientId}
                  onClientIdChange={(value) => {
                    setClientId(value);
                    const error = validateClientId(value);
                    setClientIdError(error);
                  }}
                  clientSecret={clientSecret}
                  onClientSecretChange={(value) => {
                    setClientSecret(value);
                    const error = validateClientSecret(value);
                    setClientSecretError(error);
                  }}
                  clientIdError={clientIdError}
                  clientSecretError={clientSecretError}
                />

                {authType === "oauth" && (
                  <TokenInsightsSection serverName={primaryServer.name} />
                )}

                <div className="flex justify-end pt-4">
                  <Button onClick={handleSaveConfig} disabled={!hasAuthChanges}>
                    Save Changes
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-muted/30 rounded-lg p-8 text-center">
                <p className="text-sm text-muted-foreground">
                  Authentication settings are only available for HTTP
                  connections
                </p>
              </div>
            )}
          </div>
        )}

        {/* Widget Metadata Tab */}
        {activeTab === "tools" && (
          <div className="space-y-6 max-w-3xl">
            <div>
              <div className="mb-3">
                <h3 className="text-lg font-semibold">Actions</h3>
              </div>

              {isLoadingTools && !tools ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : toolsError ? (
                toolsError === "not-connected" ? (
                  <div className="bg-muted/30 rounded-lg p-8 text-center">
                    <p className="text-sm text-muted-foreground">
                      Connect to see widget metadata
                    </p>
                  </div>
                ) : (
                  <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/30 rounded-lg p-4 text-sm text-red-600 dark:text-red-400">
                    {toolsError}
                  </div>
                )
              ) : tools && tools.tools.length > 0 ? (
                (() => {
                  const toolsWithMetadata = tools.tools.filter(
                    (tool) => tools.toolsMetadata?.[tool.name],
                  );

                  if (toolsWithMetadata.length === 0) {
                    return (
                      <div className="bg-muted/30 rounded-lg p-8 text-center">
                        <p className="text-sm text-muted-foreground">
                          No widget metadata
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-3">
                      {toolsWithMetadata.map((tool) => {
                        const metadata = tools.toolsMetadata?.[tool.name];

                        return (
                          <div
                            key={tool.name}
                            className="bg-muted/30 rounded-lg p-4 space-y-2 hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-medium text-sm">
                                  {tool.name}
                                </h4>
                                {tool.description && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {tool.description}
                                  </p>
                                )}
                              </div>
                            </div>
                            {metadata && (
                              <div className="mt-2">
                                <p className="text-xs font-medium text-muted-foreground mb-1">
                                  Widget Metadata:
                                </p>
                                <div className="text-xs">
                                  <JsonView
                                    src={metadata}
                                    collapsed={1}
                                    theme="default"
                                    style={{
                                      fontSize: "11px",
                                      backgroundColor: "transparent",
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              ) : (
                <div className="bg-muted/30 rounded-lg p-8 text-center">
                  <p className="text-sm text-muted-foreground">
                    No tools available
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Token Insights Component
function TokenInsightsSection({ serverName }: { serverName: string }) {
  const [showTokenInsights, setShowTokenInsights] = useState(false);
  const [expandedTokens, setExpandedTokens] = useState<Set<string>>(new Set());

  const toggleTokenExpansion = (key: string) => {
    setExpandedTokens((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const storedTokens = getStoredTokens(serverName);

  if (Object.keys(storedTokens).length === 0) {
    return null;
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setShowTokenInsights(!showTokenInsights)}
        className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2">
          {showTokenInsights ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="text-sm font-medium text-foreground">
            Stored Tokens
          </span>
        </div>
      </button>

      {showTokenInsights && (
        <div className="p-4 space-y-3 border-t border-border bg-muted/30">
          {Object.entries(storedTokens).map(([key, token]) => {
            const isExpanded = expandedTokens.has(key);
            let decodedToken = null;

            try {
              decodedToken = decodeJWT(token);
            } catch (e) {
              // Token might not be a JWT
            }

            return (
              <div
                key={key}
                className="border border-border rounded-lg overflow-hidden bg-background"
              >
                <button
                  type="button"
                  onClick={() => toggleTokenExpansion(key)}
                  className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    {isExpanded ? (
                      <ChevronDown className="h-3 w-3 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-3 w-3 text-muted-foreground" />
                    )}
                    <span className="text-xs font-mono">{key}</span>
                  </div>
                </button>

                {isExpanded && (
                  <div className="p-3 border-t border-border space-y-2">
                    {decodedToken ? (
                      <div className="text-xs">
                        <JsonView
                          src={decodedToken}
                          collapsed={1}
                          theme="default"
                          style={{
                            fontSize: "11px",
                            backgroundColor: "transparent",
                          }}
                        />
                      </div>
                    ) : (
                      <div className="text-xs font-mono break-all bg-muted p-2 rounded">
                        {token}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
